import { API_BASE_URL } from '../config/api';
import authService from './authService';

const getAuthHeaders = () => {
  const token = authService.getAccessToken();
  if (!token) throw new Error('Session expired. Please login again.');

  return {
    Authorization: `Bearer ${token}`,
    'X-Device-ID': authService.getDeviceId(),
  };
};

const handleResponse = async (response: Response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.message || 'Request failed';
    throw new Error(message);
  }
  return data;
};

const buildQuery = (params: Record<string, string | number | undefined>) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    query.append(key, String(value));
  });
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
};

const inventoryService = {
  async listMasterProducts(params: { search?: string; limit?: number; page?: number } = {}) {
    const query = buildQuery(params);
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/MasterInventory${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async createMasterProduct(payload: Record<string, unknown>) {
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/MasterInventory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  async upsertMasterWithOpeningStock(payload: Record<string, unknown>) {
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/MasterInventory/upsert-with-opening-stock`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  async updateMasterProduct(id: string, payload: Record<string, unknown>) {
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/MasterInventory/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  async deleteMasterProduct(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/MasterInventory/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async matchMasterProducts(items: Array<Record<string, unknown>>) {
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/MasterInventory/match`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ items }),
    });
    return handleResponse(response);
  },

  async listStockBatches(params: { search?: string; limit?: number; page?: number } = {}) {
    const query = buildQuery(params);
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/Stock${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async updateStockItem(id: string, payload: Record<string, unknown>) {
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/Stock/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  async previewOpeningStockImport(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/Stock/import-opening/preview`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
      },
      body: formData,
    });
    return handleResponse(response);
  },

  async commitOpeningStockImport(
    rows: Array<Record<string, unknown>>,
    mapping?: Record<string, string | null>,
  ) {
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/Stock/import-opening/commit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ rows, mapping }),
    });
    return handleResponse(response);
  },

  async listLowStock(params: { limit?: number } = {}) {
    const query = buildQuery(params);
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/Stock/low${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async getReorderSuggestions(params: { limit?: number } = {}) {
    const query = buildQuery(params);
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/Stock/reorder-suggestions${query}`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse(response);
    return data.data ?? [];
  },

  async listNearExpiry(params: { minDays?: number; maxDays?: number; limit?: number } = {}) {
    const query = buildQuery(params);
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/Stock/expiring${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  async getBatchExpiryReport(params: { days?: number; from?: string; to?: string; search?: string } = {}) {
    const queryParams: Record<string, string | number> = { days: params.days ?? 90 };
    if (params.from) queryParams.from = params.from;
    if (params.to) queryParams.to = params.to;
    if (params.search?.trim()) queryParams.search = params.search.trim();
    const query = buildQuery(queryParams);
    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/Stock/expiry-report${query}`, {
      headers: getAuthHeaders(),
    });
    const result = await handleResponse(response);
    return result?.data ?? { expiringSoon: [], expired: [], inRange: [], asOf: '', daysWindow: 90, customRange: null, search: null };
  },

  async searchMasterProducts(search: string) {
    return this.listMasterProducts({ search, limit: 10 });
  },

  async postExcelImportJob(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/api/V0/Pharmacy/MasterInventory/import-excel`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
      },
      body: formData,
    });

    const startPayload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = startPayload?.message || 'Request failed';
      throw new Error(message);
    }

    if (response.status !== 202 || !startPayload?.data?.jobId) {
      throw new Error(startPayload?.message || 'Unexpected response from import server.');
    }

    return startPayload.data.jobId as string;
  },

  /** Single poll for async import job (used by background import manager). */
  async fetchImportExcelJobStatus(jobId: string): Promise<{ ok: boolean; body: Record<string, unknown> }> {
    const r = await fetch(
      `${API_BASE_URL}/api/V0/Pharmacy/MasterInventory/import-excel/jobs/${jobId}`,
      { headers: getAuthHeaders() },
    );
    const body = (await r.json().catch(() => ({}))) as Record<string, unknown>;
    return { ok: r.ok, body };
  },

  async importExcelWithGemini(file: File) {
    const jobId = await this.postExcelImportJob(file);
    return this.pollImportExcelJob(jobId);
  },

  /** Poll until Gemini import finishes (backend returns 202 + jobId to avoid proxy 502 timeouts). */
  async pollImportExcelJob(jobId: string) {
    const deadline = Date.now() + 3 * 60 * 60 * 1000;
    const intervalMs = 2000;

    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, intervalMs));
      const { ok, body } = await this.fetchImportExcelJobStatus(jobId);
      if (!ok) {
        throw new Error(
          (typeof body?.message === 'string' && body.message) || 'Failed to check import status.',
        );
      }

      const data = body.data as Record<string, unknown> | undefined;
      const status = data?.status as string | undefined;

      if (status === 'completed' && body.success) {
        return {
          success: true,
          data: {
            products: data?.products,
            skipped: data?.skipped,
            total: data?.total,
            skippedDetails: data?.skippedDetails,
          },
        };
      }
      if (status === 'failed' || body.success === false) {
        throw new Error(
          (typeof body.message === 'string' && body.message) || 'Import failed.',
        );
      }
    }

    throw new Error('Import is taking too long. Try a smaller file or try again later.');
  },
};

export type InventoryService = typeof inventoryService;

export default inventoryService;




